import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import DeliveryCarrierLogoUpload from '../components/DeliveryCarrierLogoUpload';
import DeliveryRateBracketsEditor, {
  bracketsFromApi,
  bracketsToApiPayload,
  type RateBracketRow,
} from '../components/DeliveryRateBracketsEditor';

type CarrierBracketsState = {
  colissimo: RateBracketRow[];
  chronopost: RateBracketRow[];
  mondial_relay: RateBracketRow[];
  ups: RateBracketRow[];
};

function centsToEuroInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function euroInputToCents(value: string): number {
  const normalized = value.replace(',', '.').trim();
  const n = Number.parseFloat(normalized);
  if (!Number.isFinite(n) || n <= 0) {
    return 0;
  }
  return Math.round(n * 100);
}

function euroInputToOptionalCents(value: string): number | null {
  const normalized = value.replace(',', '.').trim();
  if (normalized === '') {
    return null;
  }
  const n = Number.parseFloat(normalized);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.round(n * 100);
}

function formatMarginHint(stripeEur: string, costEur: string): string | null {
  const stripeCents = euroInputToCents(stripeEur);
  const costCents = euroInputToOptionalCents(costEur);
  if (stripeCents < 100 || costCents === null) {
    return null;
  }
  const marginCents = stripeCents - costCents;
  const marginEur = (marginCents / 100).toFixed(2).replace('.', ',');
  const marginPercent = stripeCents > 0 ? ((marginCents / stripeCents) * 100).toFixed(1) : '0';
  return `Marge estimée : ${marginEur} € (${marginPercent} % du prix Stripe)`;
}

type CarrierLogos = {
  colissimo: string | null;
  chronopost: string | null;
  mondial_relay: string | null;
  ups: string | null;
};

type ChronopostServiceState = {
  enabled: boolean;
  amountEur: string;
  estimatedCostEur: string;
  label: string;
  productCode: string;
  requiresRelay: boolean;
};

const TYPE_ATTR_KEYS =
  /^(clothing_type|key_type|bag_type|jewelry_type|glasses_type|document_type|card_type|device_type|doudou_type|toy_type|mobility_type|species|type|category_type|type_vetement|sous_type|type_de_vetement)$/i;

function collectTypeOptions(
  attributes: Record<string, { type?: string; label?: string; options?: unknown; required?: boolean; use_in_title?: boolean; disabled?: boolean }> | undefined
): string[] {
  if (!attributes) return [];
  const out = new Set<string>();
  const pushOption = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) out.add(value.trim());
  };
  for (const [key, def] of Object.entries(attributes)) {
    if (!def || def.disabled) continue;
    if (def.type !== 'select' && def.type !== 'conditional_select') continue;
    const looksLikeType =
      TYPE_ATTR_KEYS.test(key) ||
      /type/i.test(def.label ?? '') ||
      def.required === true ||
      def.use_in_title === true;
    if (!looksLikeType) continue;
    const opts = def.options;
    if (Array.isArray(opts)) {
      opts.forEach(pushOption);
    } else if (opts && typeof opts === 'object') {
      Object.values(opts as Record<string, unknown>).forEach((list) => {
        if (Array.isArray(list)) list.forEach(pushOption);
      });
    }
  }
  return Array.from(out);
}

export default function DeliveryPricingPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['delivery-pricing'],
    queryFn: () => apiService.getDeliveryPricingAdmin(),
  });
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) =>
      (response.data?.categories ?? [])
        .filter((c: { is_active?: boolean }) => c.is_active !== false)
        .map(
          (c: {
            id: number;
            name: string;
            attributes?: Record<
              string,
              {
                type?: string;
                label?: string;
                options?: unknown;
                required?: boolean;
                use_in_title?: boolean;
                disabled?: boolean;
              }
            >;
          }) => ({
            id: c.id,
            name: c.name,
            typeOptions: collectTypeOptions(c.attributes),
          })
        ),
  });
  const categories = categoriesData ?? [];

  const [colissimoEur, setColissimoEur] = useState('18,00');
  const [mondialRelayEur, setMondialRelayEur] = useState('15,00');
  const [upsHomeEur, setUpsHomeEur] = useState('18,00');
  const [upsAccessPointEur, setUpsAccessPointEur] = useState('18,00');
  const [chronopostEnabled, setChronopostEnabled] = useState(false);
  const [mondialRelayEnabled, setMondialRelayEnabled] = useState(false);
  const [upsEnabled, setUpsEnabled] = useState(false);
  const [chronopostServices, setChronopostServices] = useState<Record<string, ChronopostServiceState>>({});
  const [carrierLogos, setCarrierLogos] = useState<CarrierLogos>({
    colissimo: null,
    chronopost: null,
    mondial_relay: null,
    ups: null,
  });
  const [rateBrackets, setRateBrackets] = useState<CarrierBracketsState>({
    colissimo: [],
    chronopost: [],
    mondial_relay: [],
    ups: [],
  });

  useEffect(() => {
    if (!data) return;
    setColissimoEur(centsToEuroInput(data.colissimo_amount_cents));
    setMondialRelayEur(centsToEuroInput(data.mondial_relay_amount_cents));
    setUpsHomeEur(centsToEuroInput(data.ups_home_amount_cents ?? 1800));
    setUpsAccessPointEur(centsToEuroInput(data.ups_access_point_amount_cents ?? 1800));
    setChronopostEnabled(data.chronopost_enabled);
    setMondialRelayEnabled(data.mondial_relay_enabled);
    setUpsEnabled(Boolean(data.ups_enabled));
    setCarrierLogos({
      colissimo: data.carrier_logos?.colissimo ?? null,
      chronopost: data.carrier_logos?.chronopost ?? null,
      mondial_relay: data.carrier_logos?.mondial_relay ?? null,
      ups: data.carrier_logos?.ups ?? null,
    });
    setRateBrackets({
      colissimo: bracketsFromApi(data.rate_brackets?.colissimo),
      chronopost: bracketsFromApi(data.rate_brackets?.chronopost),
      mondial_relay: bracketsFromApi(data.rate_brackets?.mondial_relay),
      ups: bracketsFromApi(data.rate_brackets?.ups),
    });

    const next: Record<string, ChronopostServiceState> = {};
    for (const [key, service] of Object.entries(data.chronopost_services ?? {})) {
      next[key] = {
        enabled: Boolean(service.enabled),
        amountEur: centsToEuroInput(service.amount_cents),
        estimatedCostEur:
          service.estimated_cost_cents != null
            ? centsToEuroInput(service.estimated_cost_cents)
            : '',
        label: service.label,
        productCode: service.product_code,
        requiresRelay: Boolean(service.requires_relay),
      };
    }
    setChronopostServices(next);
  }, [data]);

  const chronopostServiceEntries = useMemo(
    () => Object.entries(chronopostServices),
    [chronopostServices]
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updateDeliveryPricingAdmin({
        colissimo_amount_cents: euroInputToCents(colissimoEur),
        chronopost_enabled: chronopostEnabled,
        mondial_relay_amount_cents: euroInputToCents(mondialRelayEur),
        mondial_relay_enabled: mondialRelayEnabled,
        ups_enabled: upsEnabled,
        ups_home_amount_cents: euroInputToCents(upsHomeEur),
        ups_access_point_amount_cents: euroInputToCents(upsAccessPointEur),
        chronopost_services: Object.fromEntries(
          chronopostServiceEntries.map(([key, service]) => [
            key,
            {
              enabled: service.enabled,
              amount_cents: euroInputToCents(service.amountEur),
              estimated_cost_cents: euroInputToOptionalCents(service.estimatedCostEur),
            },
          ])
        ),
        rate_brackets: {
          colissimo: bracketsToApiPayload(rateBrackets.colissimo),
          chronopost: bracketsToApiPayload(rateBrackets.chronopost),
          mondial_relay: bracketsToApiPayload(rateBrackets.mondial_relay),
          ups: bracketsToApiPayload(rateBrackets.ups),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-pricing'] });
    },
  });

  const handleCarrierLogoChange = (carrier: keyof CarrierLogos, logoUrl: string | null) => {
    setCarrierLogos((prev) => ({ ...prev, [carrier]: logoUrl }));
    queryClient.invalidateQueries({ queryKey: ['delivery-pricing'] });
  };

  const handleSave = () => {
    const colissimoCents = euroInputToCents(colissimoEur);
    const mondialCents = euroInputToCents(mondialRelayEur);
    const upsHomeCents = euroInputToCents(upsHomeEur);
    const upsAccessPointCents = euroInputToCents(upsAccessPointEur);
    if (colissimoCents < 100 || mondialCents < 100 || upsHomeCents < 100 || upsAccessPointCents < 100) {
      return;
    }
    for (const [, service] of chronopostServiceEntries) {
      if (euroInputToCents(service.amountEur) < 100) {
        return;
      }
      const costCents = euroInputToOptionalCents(service.estimatedCostEur);
      if (service.estimatedCostEur.trim() !== '' && costCents === null) {
        return;
      }
    }
    saveMutation.mutate();
  };

  const updateChronopostService = (
    key: string,
    patch: Partial<ChronopostServiceState>
  ) => {
    setChronopostServices((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  };

  const saveButton = (
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={saveMutation.isPending}
      sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
    >
      {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer les tarifs'}
    </Button>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 860 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Tarifs livraison (Stripe)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Forfaits par défaut, grilles par tranche de poids (prix transporteur + frais Sherlook),
        logos et activation des options Chronopost.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les tarifs.
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Colissimo
          </Typography>
          <DeliveryCarrierLogoUpload
            carrier="colissimo"
            label="Colissimo"
            currentLogoUrl={carrierLogos.colissimo}
            onLogoChange={(url) => handleCarrierLogoChange('colissimo', url)}
          />
          <TextField
            label="Forfait Colissimo (€ TTC)"
            value={colissimoEur}
            onChange={(e) => setColissimoEur(e.target.value)}
            helperText="Utilisé si aucune tranche ne correspond à la catégorie / au poids."
            inputProps={{ inputMode: 'decimal' }}
            fullWidth
          />
          <DeliveryRateBracketsEditor
            carrierLabel="Colissimo"
            brackets={rateBrackets.colissimo}
            categories={categories}
            onChange={(next) => setRateBrackets((prev) => ({ ...prev, colissimo: next }))}
          />
          {saveButton}

          <Divider sx={{ my: 0.5 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
            Chronopost
          </Typography>
          <DeliveryCarrierLogoUpload
            carrier="chronopost"
            label="Chronopost"
            currentLogoUrl={carrierLogos.chronopost}
            onLogoChange={(url) => handleCarrierLogoChange('chronopost', url)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={chronopostEnabled}
                onChange={(e) => setChronopostEnabled(e.target.checked)}
              />
            }
            label="Activer Chronopost (master)"
          />

          {chronopostServiceEntries.map(([key, service]) => {
            const marginHint = formatMarginHint(service.amountEur, service.estimatedCostEur);
            const marginCents =
              marginHint !== null
                ? euroInputToCents(service.amountEur) - (euroInputToOptionalCents(service.estimatedCostEur) ?? 0)
                : null;

            return (
              <Box
                key={key}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={service.enabled}
                      onChange={(e) => updateChronopostService(key, { enabled: e.target.checked })}
                      disabled={!chronopostEnabled}
                    />
                  }
                  label={service.label || key}
                />
                <Typography variant="caption" color="text.secondary">
                  Produit {service.productCode}
                  {service.requiresRelay ? ' · relais' : ' · domicile'}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    label="Prix Stripe (€)"
                    value={service.amountEur}
                    onChange={(e) => updateChronopostService(key, { amountEur: e.target.value })}
                    disabled={!chronopostEnabled || !service.enabled}
                    fullWidth
                    inputProps={{ inputMode: 'decimal' }}
                  />
                  <TextField
                    label="Coût Chronopost estimé (€)"
                    value={service.estimatedCostEur}
                    onChange={(e) => updateChronopostService(key, { estimatedCostEur: e.target.value })}
                    disabled={!chronopostEnabled || !service.enabled}
                    placeholder="Ex. 12,50"
                    helperText="Référence contrat Chronopost (non facturé au client)."
                    fullWidth
                    inputProps={{ inputMode: 'decimal' }}
                  />
                </Stack>
                {marginHint ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: marginCents !== null && marginCents < 0 ? 'error.main' : 'success.main',
                    }}
                  >
                    {marginHint}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Renseignez le coût Chronopost pour calculer la marge par rapport au prix Stripe.
                  </Typography>
                )}
              </Box>
            );
          })}
          <DeliveryRateBracketsEditor
            carrierLabel="Chronopost"
            brackets={rateBrackets.chronopost}
            categories={categories}
            onChange={(next) => setRateBrackets((prev) => ({ ...prev, chronopost: next }))}
          />
          {saveButton}

          <Divider sx={{ my: 0.5 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
            Mondial Relay
          </Typography>
          <DeliveryCarrierLogoUpload
            carrier="mondial_relay"
            label="Mondial Relay"
            currentLogoUrl={carrierLogos.mondial_relay}
            onLogoChange={(url) => handleCarrierLogoChange('mondial_relay', url)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={mondialRelayEnabled}
                onChange={(e) => setMondialRelayEnabled(e.target.checked)}
              />
            }
            label="Activer Mondial Relay (Point Relais®)"
          />
          <TextField
            label="Point Relais — prix Stripe (€)"
            value={mondialRelayEur}
            onChange={(e) => setMondialRelayEur(e.target.value)}
            disabled={!mondialRelayEnabled}
            helperText="Forfait si aucune tranche ne matche."
            fullWidth
            inputProps={{ inputMode: 'decimal' }}
          />
          <DeliveryRateBracketsEditor
            carrierLabel="Mondial Relay"
            brackets={rateBrackets.mondial_relay}
            categories={categories}
            onChange={(next) => setRateBrackets((prev) => ({ ...prev, mondial_relay: next }))}
          />
          {saveButton}

          <Divider sx={{ my: 0.5 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
            UPS
          </Typography>
          <DeliveryCarrierLogoUpload
            carrier="ups"
            label="UPS"
            currentLogoUrl={carrierLogos.ups}
            onLogoChange={(url) => handleCarrierLogoChange('ups', url)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={upsEnabled}
                onChange={(event) => setUpsEnabled(event.target.checked)}
              />
            }
            label="Activer UPS (domicile + Access Point)"
          />
          <TextField
            label="UPS domicile — prix Stripe (€)"
            value={upsHomeEur}
            onChange={(e) => setUpsHomeEur(e.target.value)}
            disabled={!upsEnabled}
            fullWidth
            inputProps={{ inputMode: 'decimal' }}
          />
          <TextField
            label="UPS Access Point — prix Stripe (€)"
            value={upsAccessPointEur}
            onChange={(e) => setUpsAccessPointEur(e.target.value)}
            disabled={!upsEnabled}
            fullWidth
            inputProps={{ inputMode: 'decimal' }}
          />
          <DeliveryRateBracketsEditor
            carrierLabel="UPS"
            brackets={rateBrackets.ups}
            categories={categories}
            onChange={(next) => setRateBrackets((prev) => ({ ...prev, ups: next }))}
          />
          {saveButton}

          {saveMutation.isError && (
            <Alert severity="error">
              Enregistrement impossible. Vérifiez les forfaits (min. 1,00 €) et les tranches (poids max ≥ min).
            </Alert>
          )}
          {saveMutation.isSuccess && (
            <Alert severity="success">
              Tarifs enregistrés — Colissimo :{' '}
              {(saveMutation.data?.rate_brackets?.colissimo ?? data?.rate_brackets?.colissimo ?? []).length}{' '}
              tranche(s).
            </Alert>
          )}

          {data?.updated_at && (
            <Typography variant="caption" color="text.secondary">
              Dernière mise à jour : {data.updated_at}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
